// Import custom error classes
import errorCodes from '@/constants/errorCodes'
import UserAccountExpiredException from '@/errors/UserAccountExpiredException'
import InvalidRequestDataException from '@/errors/InvalidRequestDataException'
import TokenMalformedException from '@/errors/TokenMalformedError'
import TokenExpiredException from '@/errors/TokenExpiredException'
import TokenInvalidException from '@/errors/TokenInvalidException'
import InvalidOtpException from '@/errors/InvalidOtpException'
import ExpiredOtpException from '@/errors/ExpiredOtpException'
import { AxiosError } from 'axios'
import { appRouter } from '@/router'
import ForbiddenPortalRequested from '@/errors/ForbiddenPortalRequestedException'
import EntityDoesNotExistException from '@/errors/EntityDoesNotExistException'
import EntityAlreadyExistsException from '@/errors/EntityAlreadyExistsException'
import InvalidCustomAttributeOperationException from '@/errors/InvalidCustomAttributeOperationException'
import InvalidBusinessProfileRequestedError from '@/errors/InvalidBusinessProfileRequestedError'
import NotifyError from '@/utils/NotifyError'

// Global error handler
export const handleError = (error: AxiosError | Error) => {
  if (
    error?.name === 'AuthRefreshError' ||
    ('isAuthRefreshError' in error && Boolean(error.isAuthRefreshError))
  ) {
    handleInvalidRequest(error)
    return Promise.reject(error)
  }

  if ('response' in error && error.response) {
    // Extract error code from the response
    const errorData = extractErrorData(error)

    // Switch case for different error codes
    switch (errorData.errorCode) {
      case errorCodes.UserAccountExpiredException:
        return Promise.reject(new UserAccountExpiredException(error.message))
      case errorCodes.InvalidRequestDataException:
      case errorCodes.HttpBadRequest:
        return Promise.reject(
          new InvalidRequestDataException(errorData.message || error.message)
        )
      case errorCodes.TokenMalformedException:
      case errorCodes.TokenInvalidException:
        handleInvalidRequest(error)
        return Promise.reject(new TokenMalformedException(error.message))
      case errorCodes.TokenExpiredException:
      case errorCodes.RefreshTokenExpiredException:
        handleInvalidRequest(error)
        return Promise.reject(new TokenExpiredException(error.message))
      case errorCodes.HttpForbidden:
      case errorCodes.ForbiddenBusinessProfileRequested:
      case errorCodes.InvalidBusinessProfileRequested:
        return Promise.reject(
          new InvalidBusinessProfileRequestedError(error.message)
        )
      case errorCodes.Unauthorized:
        handleInvalidRequest(error)
        return Promise.reject(new TokenInvalidException(error.message))
      case errorCodes.ForbiddenPortalRequested:
      case errorCodes.InvalidPortalRequestedException:
        handleInvalidRequest(error)
        return Promise.reject(new ForbiddenPortalRequested(error.message))
      case errorCodes.InvalidOtpException:
        return Promise.reject(new InvalidOtpException(error.message))
      case errorCodes.ExpiredOtpException:
        return Promise.reject(new ExpiredOtpException(error.message))
      case errorCodes.EntityDoesNotExistException:
      case errorCodes.HttpEntityNotFound:
        return Promise.reject(new EntityDoesNotExistException(error.message))
      case errorCodes.EntityAlreadyExistsError:
        return Promise.reject(
          new EntityAlreadyExistsException(errorData.message || error.message)
        )
      case errorCodes.InvalidCustomAttributeOperationException:
        return Promise.reject(
          new InvalidCustomAttributeOperationException(
            errorData.message || error.message
          )
        )
      default:
        return Promise.reject(error)
    }
  } else {
    return Promise.reject(error)
  }
}
const unauthenticatedApiEndpoints = ['/v1.0/portals']

const accessErrors = new Set<number | undefined>([
  errorCodes.ForbiddenBusinessProfileRequested,
  errorCodes.HttpForbidden,
  errorCodes.Unauthorized,
  errorCodes.ForbiddenPortalRequested,
  errorCodes.InvalidPortalRequestedException,
  errorCodes.InvalidBusinessProfileRequested
])

async function handleInvalidRequest(error: AxiosError | Error) {
  if (!appRouter) return

  if (error instanceof AxiosError || error?.name === 'AxiosError') {
    const requestUrl = (error as AxiosError).config?.url
    const errorCode = extractErrorData(error as AxiosError).errorCode

    if (
      requestUrl &&
      (unauthenticatedApiEndpoints.includes(requestUrl) ||
        accessErrors.has(errorCode))
    ) {
      const isForbiddenPortal = isForbiddenPortalError(error)
      appRouter.push({
        name: isForbiddenPortal ? 'NoAccess' : 'NotFound',
        ...(!isForbiddenPortal
          ? { params: { pathMatch: appRouter.currentRoute.value.path } }
          : {}),
        replace: true
      })
      return
    }
  }

  const currentRoutePath = appRouter.currentRoute.value.fullPath
  let loginRoute = window.location.origin + '/login'
  if (currentRoutePath && currentRoutePath !== '/') {
    loginRoute += `?redirect=${encodeURIComponent(currentRoutePath)}`
  }

  try {
    const { authService } = await import('@/services/authService')
    await authService.logout()
    return
  } catch (e) {
    NotifyError(error as Error)
  }

  appRouter.push({
    path: '/login',
    query: { redirect: currentRoutePath }
  })
}

function extractErrorCode(data: unknown, status?: number): number | undefined {
  if (data && typeof data === 'object' && 'errorCode' in data) {
    const { errorCode } = data as { errorCode: unknown }
    if (typeof errorCode === 'number') return errorCode
  }
  return status
}

function extractErrorMessage(data: unknown): string {
  if (data && typeof data === 'object' && 'message' in data) {
    const { message } = data as { message: unknown }
    if (typeof message === 'string') return message
  }
  if (typeof data === 'string') return data
  return 'Something went wrong. Please try again or contact support.'
}

export function extractErrorData(error: AxiosError | unknown) {
  const axiosError = error as AxiosError
  const rawData = axiosError?.response?.data
  return {
    errorCode: extractErrorCode(rawData, axiosError?.response?.status),
    message: extractErrorMessage(rawData)
  }
}

export function isForbiddenPortalError(error: unknown): boolean {
  if (error instanceof ForbiddenPortalRequested) return true
  const { errorCode } = extractErrorData(error)
  return (
    errorCode === errorCodes.ForbiddenPortalRequested ||
    errorCode === errorCodes.InvalidPortalRequestedException ||
    errorCode === errorCodes.HttpForbidden
  )
}
